import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DASHBOARD_MAP_EXTRA_STYLES } from '../utils/dashboard/constants';
import {
  buildRescueSearchResults,
  clearRescueSearchTempMarker,
  flyMapToRescueSearchWgs,
  placeRescueSearchTempMarker
} from '../utils/dashboard/rescueSearchTempMarker';

/**
 * 地图左上角：底图样式切换 + 救援数据按钮 + 救援搜索
 * 混合按钮下拉高程/地形；选中后临时替换混合按钮文字。
 * @param {'desktop'|'mobile'} variant
 */
export default function DashboardMapStyleRescueBar({
  variant = 'desktop',
  mapStyles = [],
  currentMapStyleId,
  currentBasemapId,
  styleModeAvailability = {},
  onSwitchMapStyle,
  rescueTargets = [],
  rescueTracks = [],
  onToggleRescue,
  onShowTargetsList,
  onShowTracksList,
  getMap = null,
  onRescueSearchSetTarget = null
}) {
  const compact = variant === 'mobile';
  const [hybridMenuOpen, setHybridMenuOpen] = useState(false);
  const hybridMenuRef = useRef(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchWrapRef = useRef(null);
  const searchInputRef = useRef(null);
  const tempMarkerHolderRef = useRef({});
  const setTargetRef = useRef(onRescueSearchSetTarget);
  setTargetRef.current = onRescueSearchSetTarget;

  const extraSelected = DASHBOARD_MAP_EXTRA_STYLES.find((style) => style.id === currentMapStyleId);
  const extraAvailable = DASHBOARD_MAP_EXTRA_STYLES.filter(
    (style) => styleModeAvailability[style.id]?.available === true
  );
  const hybridActive = currentMapStyleId === 'hybrid' || Boolean(extraSelected);

  const searchResults = useMemo(
    () => buildRescueSearchResults(searchQuery, rescueTargets, rescueTracks),
    [searchQuery, rescueTargets, rescueTracks]
  );

  const clearTempMarker = () => {
    clearRescueSearchTempMarker(tempMarkerHolderRef.current);
  };

  const closeSearchPanel = () => {
    setSearchOpen(false);
    setSearchQuery('');
    clearTempMarker();
  };

  useEffect(() => {
    setHybridMenuOpen(false);
  }, [currentBasemapId]);

  useEffect(() => {
    if (!hybridMenuOpen) return undefined;
    const onPointerDown = (event) => {
      if (hybridMenuRef.current && !hybridMenuRef.current.contains(event.target)) {
        setHybridMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [hybridMenuOpen]);

  useEffect(() => {
    if (!searchOpen) return undefined;
    const onPointerDown = (event) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(event.target)) {
        closeSearchPanel();
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeSearchPanel();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    const t = setTimeout(() => searchInputRef.current?.focus?.(), 30);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [searchOpen]);

  useEffect(() => () => clearTempMarker(), []);

  // 改搜时清掉旧临时水滴，避免幽灵点
  useEffect(() => {
    clearTempMarker();
  }, [searchQuery]);

  const wrapStyle = {
    position: 'absolute',
    top: 10,
    left: 10,
    zIndex: 11,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: compact ? 4 : 8
  };
  const btnStyle = compact
    ? { fontSize: 11, padding: '3px 6px', minWidth: '70px' }
    : { fontSize: '12px', padding: '6px 12px', minWidth: '80px' };
  const styleBtnStyle = compact ? { fontSize: 12, padding: '4px 8px' } : undefined;
  const searchBtnStyle = compact
    ? { fontSize: 12, padding: '3px 8px', minWidth: 34, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }
    : { fontSize: '12px', padding: '6px 10px', minWidth: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };

  const clearTargetsTooltipLater = (delayMs, requireNotHover) => {
    setTimeout(() => {
      const tooltip = document.querySelector('.rescue-targets-list-tooltip');
      if (!tooltip) return;
      if (requireNotHover && tooltip.matches(':hover')) return;
      tooltip.remove();
    }, delayMs);
  };

  const clearTracksTooltipLater = (delayMs, requireNotHover) => {
    setTimeout(() => {
      const tooltip = document.querySelector('.rescue-tracks-list-tooltip');
      if (!tooltip) return;
      if (requireNotHover && tooltip.matches(':hover')) return;
      tooltip.remove();
    }, delayMs);
  };

  const pickStyle = (styleId) => {
    setHybridMenuOpen(false);
    onSwitchMapStyle?.(styleId);
  };

  const onHybridSlotClick = (unavailable) => {
    if (unavailable) return;
    if (extraAvailable.length === 0) {
      pickStyle('hybrid');
      return;
    }
    if (currentMapStyleId !== 'hybrid' && !extraSelected) {
      onSwitchMapStyle?.('hybrid');
    }
    setHybridMenuOpen((open) => !open);
  };

  const handleSelectResult = (item) => {
    if (!item) return;
    const map = typeof getMap === 'function' ? getMap() : null;
    if (!map) return;
    clearTempMarker();
    if (item.placeTemp) {
      placeRescueSearchTempMarker(map, item.lat, item.lng, {
        holder: tempMarkerHolderRef.current,
        onSetAsTarget: (lat, lng) => {
          try {
            setTargetRef.current?.(lat, lng);
          } finally {
            // 设目标后清临时点并收起面板，避免幽灵水滴
            clearTempMarker();
            setSearchOpen(false);
            setSearchQuery('');
          }
        },
        fly: true,
        title: '搜索坐标'
      });
      return;
    }
    flyMapToRescueSearchWgs(map, item.lat, item.lng);
  };

  const handleSearchSubmit = (event) => {
    event?.preventDefault?.();
    if (!searchResults.length) return;
    handleSelectResult(searchResults[0]);
  };

  const MagnifierIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" style={{ display: 'block' }}>
      <circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M16.2 16.2L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );

  return (
    <div style={wrapStyle}>
      <div style={{ position: 'relative', display: 'inline-block' }} ref={hybridMenuRef}>
        <div className="btn-group" style={{ borderRadius: 6, marginBottom: 0 }}>
          {mapStyles.map((style) => {
            const modeInfo = styleModeAvailability[style.id] || {};
            const unavailable = modeInfo.available === false;
            if (style.id === 'hybrid') {
              const label = extraSelected ? extraSelected.name : style.name;
              return (
                <button
                  key={style.id}
                  type="button"
                  className={hybridActive ? 'active' : ''}
                  onClick={() => onHybridSlotClick(unavailable)}
                  disabled={unavailable}
                  title={unavailable ? (modeInfo.hint || '当前图源不支持该样式') : '混合地图，可下拉高程/地形'}
                  aria-haspopup={extraAvailable.length > 0 ? 'menu' : undefined}
                  aria-expanded={hybridMenuOpen}
                  style={{
                    ...styleBtnStyle,
                    ...(unavailable ? { opacity: 0.45, cursor: 'not-allowed' } : null)
                  }}
                >
                  {label}
                  {extraAvailable.length > 0 ? (
                    <span style={{ marginLeft: 4, fontSize: 9, opacity: 0.85 }}>?</span>
                  ) : null}
                </button>
              );
            }
            return (
              <button
                key={style.id}
                type="button"
                className={currentMapStyleId === style.id ? 'active' : ''}
                onClick={() => pickStyle(style.id)}
                disabled={unavailable}
                title={unavailable ? (modeInfo.hint || '当前图源不支持该样式') : style.name}
                style={{
                  ...styleBtnStyle,
                  ...(unavailable ? { opacity: 0.45, cursor: 'not-allowed' } : null)
                }}
              >
                {style.name}
              </button>
            );
          })}
        </div>
        {hybridMenuOpen && extraAvailable.length > 0 ? (
          <div
            role="menu"
            style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              right: 0,
              minWidth: compact ? 88 : 96,
              background: 'rgba(255, 255, 255, 0.98)',
              border: '1px solid #b5d0ea',
              borderRadius: 6,
              boxShadow: '0 4px 12px rgba(0,0,0,0.14)',
              overflow: 'hidden',
              zIndex: 30
            }}
          >
            <button
              type="button"
              role="menuitem"
              className={currentMapStyleId === 'hybrid' ? 'active' : ''}
              onClick={() => pickStyle('hybrid')}
              style={{
                display: 'block',
                width: '100%',
                border: 'none',
                borderRadius: 0,
                background: currentMapStyleId === 'hybrid' ? '#1976d2' : 'transparent',
                color: currentMapStyleId === 'hybrid' ? '#fff' : '#1976d2',
                textAlign: 'left',
                ...styleBtnStyle,
                ...(compact ? null : { fontSize: 12, padding: '6px 12px' })
              }}
            >
              混合地图
            </button>
            {extraAvailable.map((style) => {
              const modeInfo = styleModeAvailability[style.id] || {};
              const selected = currentMapStyleId === style.id;
              return (
                <button
                  key={style.id}
                  type="button"
                  role="menuitem"
                  className={selected ? 'active' : ''}
                  onClick={() => pickStyle(style.id)}
                  title={modeInfo.hint || style.name}
                  style={{
                    display: 'block',
                    width: '100%',
                    border: 'none',
                    borderRadius: 0,
                    background: selected ? '#1976d2' : 'transparent',
                    color: selected ? '#fff' : '#1976d2',
                    textAlign: 'left',
                    ...styleBtnStyle,
                    ...(compact ? null : { fontSize: 12, padding: '6px 12px' })
                  }}
                >
                  {style.name}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      <div style={{ position: 'relative', display: 'inline-block' }} ref={searchWrapRef}>
        <div
          className="btn-group"
          style={{ borderRadius: 6, marginBottom: 0, ...(compact ? { marginTop: 4 } : {}) }}
        >
          <button
            type="button"
            className={rescueTargets.length > 0 ? 'active' : ''}
            onClick={() => onToggleRescue?.(rescueTargets.length > 0)}
            onMouseEnter={
              compact
                ? undefined
                : (e) => {
                    if (rescueTargets.length > 0) onShowTargetsList?.(e.target);
                  }
            }
            onMouseLeave={
              compact
                ? undefined
                : () => clearTargetsTooltipLater(100, true)
            }
            onTouchStart={
              compact
                ? (e) => {
                    if (rescueTargets.length > 0) onShowTargetsList?.(e.target);
                  }
                : undefined
            }
            onTouchEnd={
              compact
                ? () => clearTargetsTooltipLater(2000, false)
                : undefined
            }
            style={btnStyle}
          >
            {rescueTargets.length > 0 ? `救援目标(${rescueTargets.length})` : '加载救援'}
          </button>
          <button
            type="button"
            className={rescueTracks.length > 0 ? 'active' : ''}
            onClick={() => onToggleRescue?.(rescueTracks.length > 0)}
            onMouseEnter={
              compact
                ? undefined
                : (e) => {
                    if (rescueTracks.length > 0) onShowTracksList?.(e.target);
                  }
            }
            onMouseLeave={
              compact
                ? undefined
                : () => clearTracksTooltipLater(100, true)
            }
            onTouchStart={
              compact
                ? (e) => {
                    if (rescueTracks.length > 0) onShowTracksList?.(e.target);
                  }
                : undefined
            }
            onTouchEnd={
              compact
                ? () => clearTracksTooltipLater(2000, false)
                : undefined
            }
            style={btnStyle}
          >
            {rescueTracks.length > 0 ? `救援轨迹(${rescueTracks.length})` : '加载轨迹'}
          </button>
          <button
            type="button"
            className={searchOpen ? 'active' : ''}
            title="搜索救援目标 / 救援名称，或输入经纬度"
            aria-label="搜索救援"
            aria-expanded={searchOpen}
            onClick={() => {
              setSearchOpen((open) => {
                if (open) {
                  setSearchQuery('');
                  clearTempMarker();
                  return false;
                }
                return true;
              });
            }}
            style={searchBtnStyle}
          >
            {MagnifierIcon}
          </button>
        </div>

        {searchOpen ? (
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              left: 0,
              width: compact ? 260 : 300,
              background: 'rgba(255,255,255,0.98)',
              border: '1px solid #b5d0ea',
              borderRadius: 8,
              boxShadow: '0 6px 18px rgba(0,0,0,0.16)',
              zIndex: 40,
              padding: 10
            }}
          >
            <form onSubmit={handleSearchSubmit}>
              <input
                ref={searchInputRef}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜救援目标/名称，或 lat,lng"
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  padding: compact ? '6px 8px' : '8px 10px',
                  fontSize: compact ? 12 : 13,
                  outline: 'none'
                }}
              />
            </form>
            <div style={{ marginTop: 8, maxHeight: 220, overflowY: 'auto' }}>
              {!searchQuery.trim() ? (
                <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
                  可搜索已加载的救援目标名称、救援名称；也可直接输入经纬度（支持空格/中英文逗号）。
                </div>
              ) : searchResults.length === 0 ? (
                <div style={{ fontSize: 12, color: '#94a3b8' }}>无匹配结果</div>
              ) : (
                searchResults.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => handleSelectResult(item)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      border: 'none',
                      background: item.kind === 'coord' ? 'rgba(245,158,11,0.12)' : 'transparent',
                      borderRadius: 6,
                      padding: '8px 8px',
                      cursor: 'pointer',
                      marginBottom: 4
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = item.kind === 'coord'
                        ? 'rgba(245,158,11,0.22)'
                        : 'rgba(25,118,210,0.08)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = item.kind === 'coord'
                        ? 'rgba(245,158,11,0.12)'
                        : 'transparent';
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{item.label}</div>
                    {item.sub ? (
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{item.sub}</div>
                    ) : null}
                  </button>
                ))
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
